import React, { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Link,
  CircularProgress,
  Typography,
  Box,
  Tabs,
  Tab,
  Tooltip,
  TablePagination
} from '@mui/material';
import { TrendingUp, TrendingDown, Settings, Business, Domain, Assessment } from '@mui/icons-material';
import api from '../functions/api';
import ThreeTablesModal from './SearchResultModal';
import HelpPanel from './HelpPanel';
import HelpIcon from './HelpIcon';
import { useContextualHelp } from '../hooks/useContextualHelp';

const Trends = forwardRef((props, ref) => {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef(null);
  const [trendsData, setTrendsData] = useState([]);
  const [sectorTrendsData, setSectorTrendsData] = useState([]);
  const [tickerAggregatesData, setTickerAggregatesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('article_date');
  const [order, setOrder] = useState('desc');
  const [tabValue, setTabValue] = useState(2);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [selectedTicker, setSelectedTicker] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Contextual help hook
  const { openHelp, helpOpen, helpData, helpLoading, helpError, closeHelp } = useContextualHelp('trendsearch.tickerAggregates');
  
  // Article modal states
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [articleData, setArticleData] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [selectedTickerForArticles, setSelectedTickerForArticles] = useState('');
  const [selectedColumn, setSelectedColumn] = useState('');
  const [sentimentCalcCache, setSentimentCalcCache] = useState({});
  const [loadingCalc, setLoadingCalc] = useState({});

  useImperativeHandle(ref, () => ({
    showModal: () => {
      setOpen(true);
      fetchTrendsData();
      fetchSectorTrendsData();
      fetchTickerAggregatesData();
    }
  }));

  const fetchTrendsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get company trends data
      const response = await api.get('/trendsearch_all_data');
      
      if (response.data.success) {
        setTrendsData(response.data.data);
      } else {
        setError('Failed to load trends data');
      }
    } catch (err) {
      console.error('Error fetching trends data:', err);
      setError('Error loading trends data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSectorTrendsData = async () => {
    try {
      // Get sector trends data (market_change impact type)
      const response = await api.get('/trendsearch_sector_data');
      
      if (response.data.success) {
        setSectorTrendsData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching sector trends data:', err);
      // Don't set error here as company data might still work
    }
  };

  const fetchTickerAggregatesData = async () => {
    try {
      // Get ticker aggregate data
      const response = await api.get('/trendsearch_ticker_aggregates');
      
      if (response.data.success) {
        setTickerAggregatesData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching ticker aggregates data:', err);
      // Don't set error here as other data might still work
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleTabChange = () => {};

  const handleTickerClick = (ticker, companyName) => {
    if (ticker && ticker !== 'N/A') {
      // If we have a ticker, open the search modal
      setSelectedTicker(ticker);
      setSearchModalOpen(true);
    } else if (companyName) {
      // If no ticker but we have company name, show message
      alert(`No ticker available for ${companyName}. Please populate tickers first.`);
    }
  };

  const handleSearchModalClose = () => {
    setSearchModalOpen(false);
    setSelectedTicker('');
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (orderBy === 'article_date') {
      return new Date(b[orderBy]) - new Date(a[orderBy]);
    }
    if (orderBy === 'financial_metric') {
      return (b[orderBy] || 0) - (a[orderBy] || 0);
    }
    if (orderBy === 'timeline_months') {
      return (b[orderBy] || 0) - (a[orderBy] || 0);
    }
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  const sortedData = React.useMemo(() => {
    const dataToSort = tickerAggregatesData;
    return [...dataToSort].sort(getComparator(order, orderBy));
  }, [tickerAggregatesData, order, orderBy]);

  const paginatedData = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const getImpactIcon = (impactType) => {
    switch (impactType) {
      case 'company_winner':
        return <TrendingUp style={{ color: '#4caf50', fontSize: '18px' }} />;
      case 'company_loser':
        return <TrendingDown style={{ color: '#f44336', fontSize: '18px' }} />;
      case 'company_enabler':
        return <Settings style={{ color: '#2196f3', fontSize: '18px' }} />;
      default:
        return null;
    }
  };

  const getImpactColor = (impactType) => {
    switch (impactType) {
      case 'company_winner':
        return 'success';
      case 'company_loser':
        return 'error';
      case 'company_enabler':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatImpactType = (impactType) => {
    switch (impactType) {
      case 'company_winner':
        return 'Winner';
      case 'company_loser':
        return 'Loser';
      case 'company_enabler':
        return 'Enabler';
      default:
        return impactType;
    }
  };

  const formatMetric = (metric, metricType) => {
    if (!metric || !metricType) return 'N/A';
    
    if (metric >= 1000000000) {
      return `$${(metric / 1000000000).toFixed(1)}B ${metricType}`;
    } else if (metric >= 1000000) {
      return `$${(metric / 1000000).toFixed(1)}M ${metricType}`;
    } else {
      return `$${metric.toLocaleString()} ${metricType}`;
    }
  };

  const formatTimeline = (months) => {
    if (!months) return 'Immediate';
    if (months <= 1) return 'Next month';
    if (months <= 12) return `${months} months`;
    const years = Math.floor(months / 12);
    return `${years} year${years > 1 ? 's' : ''}`;
  };

  const formatTimelineShort = (timelineString) => {
    if (!timelineString) return 'N/A';
    // Parse "Immediate (2) | Medium (1) | Long-term (0)" format
    const matches = timelineString.match(/Immediate \((\d+)\).*Medium \((\d+)\).*Long-term \((\d+)\)/);
    if (matches) {
      const [, immediate, medium, longTerm] = matches;
      return `I: ${immediate}, M: ${medium}, LT: ${longTerm}`;
    }
    return timelineString;
  };

  const buildSentimentTooltip = (row) => {
    try {
      const parts = [];
      const score = parseFloat(row.overall_sentiment);
      if (!Number.isNaN(score)) {
        parts.push(`Overall: ${score.toFixed(2)}`);
      }
      const count = (row.sentiment_record_count ?? row.total_impacts ?? 0);
      if (count) {
        parts.push(`Avg across ${count} record${count === 1 ? '' : 's'}`);
      }
      const w = row.winner_count ?? 0;
      const l = row.loser_count ?? 0;
      const e = row.enabler_count ?? 0;
      parts.push(`Mix W/L/E: ${w}/${l}/${e}`);
      const timelineShort = formatTimelineShort(row.trend_timeline);
      if (timelineShort && timelineShort !== 'N/A') {
        parts.push(`Timeline: ${timelineShort}`);
      }
      if (row.recent_activity_7d !== undefined && row.recent_activity_7d !== null) {
        parts.push(`Recent 7d: ${row.recent_activity_7d}`);
      }
      if (row.latest_impact_date) {
        parts.push(`Latest: ${row.latest_impact_date}`);
      }
      return parts.join(' • ');
    } catch (_e) {
      return '';
    }
  };

  const computeCalcFromArticles = (articles) => {
    if (!Array.isArray(articles) || articles.length === 0) return null;
    let nFinal = 0, sumFinal = 0;
    let nRaw = 0, sumRaw = 0;
    let nBusAdj = 0, sumBusAdj = 0; // average of (business - raw)
    let nDecay = 0, sumDecay = 0;
    for (const a of articles) {
      const raw = a.raw_sentiment_score;
      const bus = a.sentiment_score; // business-adjusted pre-decay
      const decayAdj = a.time_decay_adjustment;
      const final = a.final_sentiment_score;
      if (raw !== null && raw !== undefined) { nRaw += 1; sumRaw += Number(raw); }
      if (bus !== null && bus !== undefined && raw !== null && raw !== undefined) { nBusAdj += 1; sumBusAdj += (Number(bus) - Number(raw)); }
      if (decayAdj !== null && decayAdj !== undefined) { nDecay += 1; sumDecay += Number(decayAdj); }
      if (final !== null && final !== undefined) { nFinal += 1; sumFinal += Number(final); }
    }
    if (nFinal === 0 && nRaw === 0) return null;
    const avgFinal = nFinal ? (sumFinal / nFinal) : null;
    const avgRaw = nRaw ? (sumRaw / nRaw) : null;
    const avgBusAdj = nBusAdj ? (sumBusAdj / nBusAdj) : null;
    const avgDecayAdj = nDecay ? (sumDecay / nDecay) : null;
    // Debug log of computed components
    // eslint-disable-next-line no-console
    console.log('[SentimentTooltip] compute', { nFinal, avgFinal, nRaw, avgRaw, nBusAdj, avgBusAdj, nDecay, avgDecayAdj });
    return { avgFinal, nFinal, avgRaw, nRaw, avgBusAdj, nBusAdj, avgDecayAdj, nDecay };
  };

  const renderSentimentTooltip = (row) => {
    const entry = sentimentCalcCache[row.ticker];
    if (loadingCalc[row.ticker]) return 'Calculating...';
    if (!entry) return 'Hover to calculate';
    if (typeof entry === 'string') return entry;
    const { avgFinal, nFinal, avgRaw, nRaw, avgBusAdj, avgDecayAdj } = entry;
    return (
      <Box sx={{ fontSize: '12px', maxWidth: 520, whiteSpace: 'normal', lineHeight: 1.4 }}>
        {avgFinal !== null && (
          <div><strong>Overall sentiment</strong> = average of Final scores across recent articles → {avgFinal.toFixed(2)} (n={nFinal || 0})</div>
        )}
        <Box sx={{ mt: 1 }}>
          <div style={{ fontWeight: 700 }}>How we get a Final score per article</div>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            {avgRaw !== null && (
              <li>
                <strong>Model guess</strong> (from the article) — average {avgRaw.toFixed(2)} (n={nRaw || 0}).
              </li>
            )}
            {avgBusAdj !== null && (
              <li>
                <strong>Rule adjustment</strong> — average {avgBusAdj >= 0 ? '+' : ''}{avgBusAdj.toFixed(2)}. We fix obvious signs (winners non‑negative, losers non‑positive) and nudge far‑future scores toward the middle.
              </li>
            )}
            {avgDecayAdj !== null && (
              <li>
                <strong>Age adjustment</strong> — average {avgDecayAdj >= 0 ? '+' : ''}{avgDecayAdj.toFixed(2)}. Older articles count less; the adjustment pulls scores toward 0 over time.
              </li>
            )}
            <li><strong>Formula</strong>: Final = Model guess + Rule adjustment + Age adjustment</li>
          </ul>
        </Box>
      </Box>
    );
  };

  const ensureSentimentCalc = async (ticker) => {
    if (!ticker) return;
    if (sentimentCalcCache[ticker]) return;
    try {
      setLoadingCalc((s) => ({ ...s, [ticker]: true }));
      // eslint-disable-next-line no-console
      console.log('[SentimentTooltip] fetch start', { ticker, days: 180 });
      let response = await api.get('/trendsearch_article_details', { params: { ticker, days: 180 } });
      let arts = response?.data?.data || [];
      // eslint-disable-next-line no-console
      console.log('[SentimentTooltip] fetch done', { ticker, count: arts.length, sample: arts.slice(0, 2) });
      if (!arts.length) {
        // Fallback: widen horizon
        // eslint-disable-next-line no-console
        console.log('[SentimentTooltip] widening window to 365d', { ticker });
        response = await api.get('/trendsearch_article_details', { params: { ticker, days: 365 } });
        arts = response?.data?.data || [];
        // eslint-disable-next-line no-console
        console.log('[SentimentTooltip] fetch done (365d)', { ticker, count: arts.length, sample: arts.slice(0, 2) });
      }
      const calc = computeCalcFromArticles(arts);
      // eslint-disable-next-line no-console
      console.log('[SentimentTooltip] result', { ticker, calc });
      setSentimentCalcCache((s) => ({ ...s, [ticker]: calc || 'No sentiment records' }));
    } catch (_e) {
      // eslint-disable-next-line no-console
      console.log('[SentimentTooltip] fetch error', { ticker: ticker, error: _e });
      setSentimentCalcCache((s) => ({ ...s, [ticker]: 'Unable to calculate' }));
    } finally {
      setLoadingCalc((s) => ({ ...s, [ticker]: false }));
    }
  };

  const handleArticleClick = async (ticker, impactType, column) => {
    try {
      setLoadingArticles(true);
      const params = { ticker };
      
      // Add impact type filter based on column
      if (column === 'winner_count') params.impact_type = 'company_winner';
      else if (column === 'loser_count') params.impact_type = 'company_loser';
      else if (column === 'enabler_count') params.impact_type = 'company_enabler';
      else if (column === 'market_change_count') params.impact_type = 'market_change';
      
      const response = await api.get('/trendsearch_article_details', { params });
      
      if (response.data.success) {
        setArticleData(response.data.data);
        setSelectedTickerForArticles(ticker);
        setSelectedColumn(column);
        setArticleModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoadingArticles(false);
    }
  };

  const formatSentiment = (score) => {
    if (score === null || score === undefined) return 'N/A';
    const color = score >= 5 ? '#4caf50' : score >= 0 ? '#ff9800' : '#f44336';
    const emoji = score >= 5 ? '😊' : score >= 0 ? '😐' : '😞';
    return { score, color, emoji };
  };

  const calculateAdjustment = (rawScore, finalScore) => {
    if (rawScore === null || rawScore === undefined || finalScore === null || finalScore === undefined) {
      return 'N/A';
    }
    const adjustment = finalScore - rawScore;
    const color = adjustment > 0 ? '#4caf50' : adjustment < 0 ? '#f44336' : '#757575';
    const sign = adjustment > 0 ? '+' : '';
    return { adjustment, color, sign };
  };

  const companyColumns = [
    { id: 'ticker', label: 'Ticker', width: '80px' },
    { id: 'company_name', label: 'Company', width: '180px' },
    { id: 'impact_type', label: 'Impact', width: '100px' },
    { id: 'financial_metric', label: 'Metric', width: '130px' },
    { id: 'metric_type', label: 'Type', width: '90px' },
    { id: 'raw_sentiment_score', label: 'Raw', width: '50px' },
    { id: 'sentiment_adjustment', label: 'Bus', width: '50px' },
    { id: 'sentiment_score', label: 'Adj', width: '50px' },
    { id: 'decay_percentage', label: 'Decay%', width: '60px' },
    { id: 'time_decay_adjustment', label: 'Decay', width: '50px' },
    { id: 'final_sentiment_score', label: 'Final', width: '60px' },
    { id: 'timeline_months', label: 'Timeline', width: '80px' },
    { id: 'article_date', label: 'Date', width: '100px' },
    { id: 'source_name', label: 'Source', width: '100px' }
  ];

  const sectorColumns = [
    { id: 'sector', label: 'Sector', width: '110px' },
    { id: 'industry', label: 'Industry', width: '160px' },
    { id: 'financial_metric', label: 'Metric', width: '130px' },
    { id: 'metric_type', label: 'Type', width: '90px' },
    { id: 'raw_sentiment_score', label: 'Raw', width: '50px' },
    { id: 'sentiment_adjustment', label: 'Bus', width: '50px' },
    { id: 'sentiment_score', label: 'Adj', width: '50px' },
    { id: 'decay_percentage', label: 'Decay%', width: '60px' },
    { id: 'time_decay_adjustment', label: 'Decay', width: '50px' },
    { id: 'final_sentiment_score', label: 'Final', width: '60px' },
    { id: 'timeline_months', label: 'Timeline', width: '80px' },
    { id: 'description', label: 'Description', width: '250px' },
    { id: 'article_date', label: 'Date', width: '100px' },
    { id: 'source_name', label: 'Source', width: '100px' }
  ];

  const tickerAggregateColumns = [
    { id: 'ticker', label: 'Ticker', width: '80px' },
    { id: 'company_name', label: 'Company', width: '150px' },
    { id: 'sector', label: 'Sector', width: '100px' },
    { id: 'industry', label: 'Industry', width: '120px' },
    { id: 'overall_sentiment', label: 'Sentiment', width: '80px' },
    { id: 'total_impacts', label: 'Total', width: '60px' },
    { id: 'winner_count', label: 'W', width: '40px' },
    { id: 'loser_count', label: 'L', width: '40px' },
    { id: 'enabler_count', label: 'E', width: '40px' },
    { id: 'market_change_count', label: 'MC', width: '50px' },
    { id: 'trend_timeline', label: 'Effect', width: '120px' },
    { id: 'recent_activity_7d', label: 'Recent', width: '60px' },
    { id: 'latest_impact_date', label: 'Latest', width: '100px' }
  ];
  const columnTooltips = {
    ticker: 'Ticker symbol',
    company_name: 'Company name',
    sector: 'Yahoo Finance sector',
    industry: 'Yahoo Finance industry',
    overall_sentiment: 'Overall time-decayed sentiment score',
    total_impacts: 'Total TrendSearch impacts counted',
    winner_count: 'Winner impacts',
    loser_count: 'Loser impacts',
    enabler_count: 'Enabler impacts',
    market_change_count: 'Market change impacts',
    trend_timeline: 'Typical effect timeline: I=Immediate, M=Medium-term, LT=Long-term',
    recent_activity_7d: 'Impacts in the last 7 days',
    latest_impact_date: 'Most recent impact date',
  };

  const columns = tickerAggregateColumns;

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      ref={dialogRef}
      PaperProps={{
        style: {
          minHeight: '75vh',
          maxHeight: '90vh',
          width: '96vw',
          position: 'relative'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          Company Aggregate Trends
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          {tabValue === 0 ? 'Company impacts identified from news sources' : 
           tabValue === 1 ? 'Sector-level market changes and trends' : 
           'Aggregated trend analysis by company/ticker'}
        </Typography>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
          <Tabs value={2}>
            <Tab 
              icon={<Assessment />} 
              label="Company Aggregate Trend" 
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          </Tabs>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ overflow: 'visible' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading trends data...
            </Typography>
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Typography variant="body1" color="error">
              {error}
            </Typography>
          </Box>
        ) : (
          <>
          <TableContainer component={Paper} sx={{ width: '100%', overflow: 'visible' }}>
            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      style={{ 
                        width: column.width,
                        minWidth: column.width,
                        maxWidth: column.width,
                        fontWeight: 'bold',
                        padding: '4px 2px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      sortDirection={orderBy === column.id ? order : false}
                    >
                      <Tooltip title={columnTooltips[column.id] || ''} placement="top">
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={() => handleSort(column.id)}
                        >
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{column.label}</span>
                            {column.id === 'overall_sentiment' && (
                              <HelpIcon onClick={() => openHelp(column.id, column.label, {})} />
                            )}
                          </Box>
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row, index) => (
                  <TableRow hover key={index}>
                    {tabValue === 0 ? (
                      // Company Trends View
                      <>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Chip 
                            label={row.ticker || 'N/A'} 
                            color="primary" 
                            size="small" 
                            variant="outlined"
                            onClick={() => handleTickerClick(row.ticker, row.company_name)}
                            style={{ cursor: row.ticker && row.ticker !== 'N/A' ? 'pointer' : 'default' }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Typography
                            variant="body2"
                            onClick={() => handleTickerClick(row.ticker, row.company_name)}
                            style={{ 
                              cursor: row.ticker && row.ticker !== 'N/A' ? 'pointer' : 'default',
                              fontWeight: 'bold',
                              color: row.ticker && row.ticker !== 'N/A' ? '#1976d2' : 'inherit'
                            }}
                          >
                            {row.company_name}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getImpactIcon(row.impact_type)}
                            <Chip
                              label={formatImpactType(row.impact_type)}
                              color={getImpactColor(row.impact_type)}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {formatMetric(row.financial_metric, row.metric_type)}
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.metric_type || 'N/A'}</TableCell>
                        {/* Raw Sentiment */}
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            const sentiment = formatSentiment(row.raw_sentiment_score);
                            if (sentiment === 'N/A') return sentiment;
                            return (
                              <span style={{ color: sentiment.color, fontWeight: 'bold' }}>
                                {sentiment.score}
                              </span>
                            );
                          })()}
                        </TableCell>
                        {/* Adjustment */}
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            const adjustment = calculateAdjustment(row.raw_sentiment_score, row.sentiment_score);
                            if (adjustment === 'N/A') return adjustment;
                            return (
                              <span style={{ color: adjustment.color, fontWeight: 'bold' }}>
                                {adjustment.sign}{adjustment.adjustment}
                              </span>
                            );
                          })()}
                        </TableCell>
                        {/* Final Sentiment */}
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            const sentiment = formatSentiment(row.sentiment_score);
                            if (sentiment === 'N/A') return sentiment;
                            return (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <span style={{ color: sentiment.color, fontWeight: 'bold' }}>
                                  {sentiment.score}
                                </span>
                                <span>{sentiment.emoji}</span>
                              </Box>
                            );
                          })()}
                        </TableCell>
                        {/* Decay Percentage - Company */}
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.decay_percentage !== null && row.decay_percentage !== undefined ? `${parseFloat(row.decay_percentage).toFixed(1)}%` : 'N/A'}
                        </TableCell>
                        {/* Time Decay Adjustment - Company */}
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            if (row.time_decay_adjustment === null || row.time_decay_adjustment === undefined) return 'N/A';
                            const adj = parseFloat(row.time_decay_adjustment);
                            const color = adj < 0 ? '#f44336' : adj > 0 ? '#4caf50' : '#757575';
                            const sign = adj > 0 ? '+' : '';
                            return (
                              <span style={{ color, fontWeight: 'bold' }}>
                                {sign}{adj.toFixed(2)}
                              </span>
                            );
                          })()}
                        </TableCell>
                        {/* Final Time-Adjusted Sentiment - Company */}
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            if (row.final_sentiment_score === null || row.final_sentiment_score === undefined) return 'N/A';
                            const finalScore = parseFloat(row.final_sentiment_score);
                            const color = finalScore >= 5 ? '#4caf50' : finalScore >= 0 ? '#ff9800' : '#f44336';
                            const emoji = finalScore >= 5 ? '😊' : finalScore >= 0 ? '😐' : '😞';
                            return (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <span style={{ color, fontWeight: 'bold' }}>
                                  {finalScore.toFixed(2)}
                                </span>
                                <span>{emoji}</span>
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatTimeline(row.timeline_months)}</TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {new Date(row.article_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.article_url ? (
                            <Link
                              href={row.article_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              color="primary"
                            >
                              {row.source_name}
                            </Link>
                          ) : (
                            row.source_name
                          )}
                        </TableCell>
                      </>
                    ) : tabValue === 1 ? (
                      // Sector Trends View
                      <>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Chip 
                            label={row.sector} 
                            color="secondary" 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.industry}</TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {formatMetric(row.financial_metric, row.metric_type)}
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.metric_type || 'N/A'}</TableCell>
                        {/* Raw Sentiment */}
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            const sentiment = formatSentiment(row.raw_sentiment_score);
                            if (sentiment === 'N/A') return sentiment;
                            return (
                              <span style={{ color: sentiment.color, fontWeight: 'bold' }}>
                                {sentiment.score}
                              </span>
                            );
                          })()}
                        </TableCell>
                        {/* Adjustment */}
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            const adjustment = calculateAdjustment(row.raw_sentiment_score, row.sentiment_score);
                            if (adjustment === 'N/A') return adjustment;
                            return (
                              <span style={{ color: adjustment.color, fontWeight: 'bold' }}>
                                {adjustment.sign}{adjustment.adjustment}
                              </span>
                            );
                          })()}
                        </TableCell>
                        {/* Final Sentiment */}
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            const sentiment = formatSentiment(row.sentiment_score);
                            if (sentiment === 'N/A') return sentiment;
                            return (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <span style={{ color: sentiment.color, fontWeight: 'bold' }}>
                                  {sentiment.score}
                                </span>
                                <span>{sentiment.emoji}</span>
                              </Box>
                            );
                          })()}
                        </TableCell>
                        {/* Decay Percentage - Sector */}
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.decay_percentage !== null && row.decay_percentage !== undefined ? `${parseFloat(row.decay_percentage).toFixed(1)}%` : 'N/A'}
                        </TableCell>
                        {/* Time Decay Adjustment - Sector */}
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            if (row.time_decay_adjustment === null || row.time_decay_adjustment === undefined) return 'N/A';
                            const adj = parseFloat(row.time_decay_adjustment);
                            const color = adj < 0 ? '#f44336' : adj > 0 ? '#4caf50' : '#757575';
                            const sign = adj > 0 ? '+' : '';
                            return (
                              <span style={{ color, fontWeight: 'bold' }}>
                                {sign}{adj.toFixed(2)}
                              </span>
                            );
                          })()}
                        </TableCell>
                        {/* Final Time-Adjusted Sentiment - Sector */}
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            if (row.final_sentiment_score === null || row.final_sentiment_score === undefined) return 'N/A';
                            const finalScore = parseFloat(row.final_sentiment_score);
                            const color = finalScore >= 5 ? '#4caf50' : finalScore >= 0 ? '#ff9800' : '#f44336';
                            const emoji = finalScore >= 5 ? '😊' : finalScore >= 0 ? '😐' : '😞';
                            return (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <span style={{ color, fontWeight: 'bold' }}>
                                  {finalScore.toFixed(2)}
                                </span>
                                <span>{emoji}</span>
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formatTimeline(row.timeline_months)}</TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Typography variant="body2" noWrap title={row.description}>
                            {row.description ? `${row.description.substring(0, 30)}...` : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {new Date(row.article_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.article_url ? (
                            <Link
                              href={row.article_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              color="primary"
                            >
                              {row.source_name}
                            </Link>
                          ) : (
                            row.source_name
                          )}
                        </TableCell>
                      </>
                    ) : (
                      // Company Aggregate Trend View
                      <>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Chip 
                            label={row.ticker} 
                            color="primary" 
                            size="small" 
                            variant="outlined"
                            onClick={() => handleTickerClick(row.ticker, row.company_name)}
                            style={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Typography
                            variant="body2"
                            onClick={() => handleTickerClick(row.ticker, row.company_name)}
                            style={{ 
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              color: '#1976d2'
                            }}
                          >
                            {row.company_name}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Chip 
                            label={row.sector} 
                            color="secondary" 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.industry}</TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Tooltip 
                            title={renderSentimentTooltip(row)}
                            placement="top"
                            arrow
                            enterDelay={200}
                            leaveDelay={200}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: '#263238',
                                  color: '#fff',
                                  border: '1px solid #90caf9',
                                  maxWidth: 560,
                                  p: 1.5,
                                  whiteSpace: 'normal',
                                  lineHeight: 1.4
                                }
                              },
                              arrow: {
                                sx: { color: '#263238' }
                              }
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'help' }} onMouseEnter={() => ensureSentimentCalc(row.ticker)}>
                              <span style={{ color: (parseFloat(row.overall_sentiment) || 0) >= 2 ? '#4caf50' : (parseFloat(row.overall_sentiment) || 0) >= -2 ? '#ff9800' : '#f44336', fontWeight: 'bold' }}>
                                {(parseFloat(row.overall_sentiment) || 0).toFixed(2)}
                              </span>
                              <span>{row.sentiment_indicator}</span>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Chip 
                            label={row.total_impacts} 
                            color="default" 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Chip 
                            label={row.winner_count} 
                            color="success" 
                            size="small" 
                            variant="outlined"
                            onClick={() => handleArticleClick(row.ticker, 'company_winner', 'winner_count')}
                            style={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Chip 
                            label={row.loser_count} 
                            color="error" 
                            size="small" 
                            variant="outlined"
                            onClick={() => handleArticleClick(row.ticker, 'company_loser', 'loser_count')}
                            style={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Chip 
                            label={row.enabler_count} 
                            color="primary" 
                            size="small" 
                            variant="outlined"
                            onClick={() => handleArticleClick(row.ticker, 'company_enabler', 'enabler_count')}
                            style={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Chip 
                            label={row.market_change_count} 
                            color="default" 
                            size="small" 
                            variant="outlined"
                            onClick={() => handleArticleClick(row.ticker, 'market_change', 'market_change_count')}
                            style={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Typography variant="body2" noWrap title={row.trend_timeline}>
                            {formatTimelineShort(row.trend_timeline)}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.recent_activity_7d > 0 ? (
                            <Chip 
                              label={row.recent_activity_7d} 
                              color="warning" 
                              size="small" 
                              variant="outlined"
                              onClick={() => handleArticleClick(row.ticker, null, 'recent_activity_7d')}
                              style={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <span style={{ color: '#757575' }}>0</span>
                          )}
                        </TableCell>
                        <TableCell style={{ padding: '4px 2px', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Typography 
                            variant="body2" 
                            onClick={() => handleArticleClick(row.ticker, null, 'latest_impact_date')}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                          >
                            {row.latest_impact_date}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display="flex" justifyContent="flex-end">
            <TablePagination
              component="div"
              count={sortedData.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* SearchResultModal for ticker clicks */}
    <ThreeTablesModal 
      tickerValue={selectedTicker}
      open={searchModalOpen}
      onClose={handleSearchModalClose}
      userId={null} // You might want to get actual userId here
    />

    <HelpPanel 
      open={helpOpen} 
      onClose={closeHelp} 
      data={helpData} 
      loading={helpLoading} 
      error={helpError}
      container={dialogRef?.current}
    />

    {/* Article Details Modal */}
    <Dialog
      open={articleModalOpen}
      onClose={() => setArticleModalOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          minHeight: '60vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Articles for {selectedTickerForArticles} - {selectedColumn === 'winner_count' ? 'Winners' : 
                                                      selectedColumn === 'loser_count' ? 'Losers' : 
                                                      selectedColumn === 'enabler_count' ? 'Enablers' : 
                                                      selectedColumn === 'market_change_count' ? 'Market Changes' : 
                                                      selectedColumn === 'recent_activity_7d' ? 'Recent Activity' : 
                                                      'Latest Articles'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loadingArticles ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading articles...
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Impact</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {articleData.map((article, index) => (
                  <TableRow key={index}>
                    <TableCell>{article.date}</TableCell>
                    <TableCell>
                      {article.article_url ? (
                        <Link
                          href={article.article_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          color="primary"
                        >
                          {article.article_title || 'View Article'}
                        </Link>
                      ) : (
                        article.article_title || 'No title'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={article.impact_type === 'company_winner' ? 'Winner' :
                               article.impact_type === 'company_loser' ? 'Loser' :
                               article.impact_type === 'company_enabler' ? 'Enabler' :
                               'Market Change'}
                        color={article.impact_type === 'company_winner' ? 'success' :
                               article.impact_type === 'company_loser' ? 'error' :
                               article.impact_type === 'company_enabler' ? 'primary' :
                               'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={article.full_description}>
                        {article.first_5_words || article.full_description?.substring(0, 50) + '...' || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell>{article.source_name || 'Unknown'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setArticleModalOpen(false)} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
});

Trends.displayName = 'Trends';

export default Trends;
